import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {Box, Button, Typography} from '@mui/material';
import ConfirmModal from '../pieces/modals/confirmPopup';
import {COLORS} from '../styles/styleConstants';
import {
  AI_CALL_INCOMING_PROMPT_BODY,
  AI_CALL_PASSED_TO_OTHER_AGENT_BODY,
} from '../utils/aiCallSession';

function getRemainingSeconds(expiresAt) {
  if (!expiresAt) {
    return 0;
  }
  return Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
}

const bodyTextSx = {
  color: COLORS.black,
  fontWeight: 600,
  fontSize: {xs: '1rem', sm: '1.05rem', md: '1.1rem'},
  lineHeight: 1.5,
};

const outlinedBtnSx = {
  minWidth: 100,
  fontSize: '0.95rem',
  fontWeight: 600,
  color: COLORS.globalBlue,
  borderColor: COLORS.globalBlue,
  '&:hover': {
    borderColor: COLORS.globalBlueHover,
    backgroundColor: COLORS.globalBluePale,
  },
};

const containedBtnSx = {
  minWidth: 100,
  fontSize: '0.95rem',
  fontWeight: 600,
  backgroundColor: COLORS.globalBlue,
  '&:hover': {
    backgroundColor: COLORS.globalBlueHover,
  },
};

/**
 * Accept/Decline popup for an incoming AI call, driven by the `aiCallIncomingPrompt`
 * object returned from {@link useUserSocket}. Renders a live countdown until the
 * server's confirmation window (`expiresAt`) elapses, then auto-closes.
 */
const AiCallIncomingPrompt = ({prompt}) => {
  const {
    open,
    status,
    hasCall,
    expiresAt,
    submitting,
    onAccept,
    onDecline,
    onExpire,
    onCloseStatus,
  } = prompt;

  const [remaining, setRemaining] = useState(() => getRemainingSeconds(expiresAt));

  useEffect(() => {
    if (!hasCall || submitting || !expiresAt) {
      return undefined;
    }
    setRemaining(getRemainingSeconds(expiresAt));
    const timer = setInterval(() => {
      const next = getRemainingSeconds(expiresAt);
      setRemaining(next);
      if (next <= 0) {
        onExpire();
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [hasCall, submitting, expiresAt, onExpire]);

  const handleClose = () => {
    if (status === 'passed') {
      onCloseStatus();
      return;
    }
    // Dismissing (X / Escape) a live incoming call counts as declining it.
    if (!submitting) {
      onDecline();
    }
  };

  return (
    <ConfirmModal
      open={open}
      handleClose={handleClose}
      hideHeader
      width={{xs: '98%', md: '520px'}}
    >
      <Box textAlign="center" px={{xs: 1, md: 2}}>
        {status === 'passed' ? (
          <>
            <Typography variant="body1" mb={3} sx={bodyTextSx}>
              {AI_CALL_PASSED_TO_OTHER_AGENT_BODY}
            </Typography>
            <Box display="flex" justifyContent="center">
              <Button
                variant="contained"
                size="medium"
                onClick={onCloseStatus}
                sx={containedBtnSx}
              >
                OK
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Typography variant="body1" mb={expiresAt ? 1 : 3} sx={bodyTextSx}>
              {AI_CALL_INCOMING_PROMPT_BODY}
            </Typography>
            {expiresAt ? (
              <Typography
                variant="h5"
                mb={3}
                sx={{color: COLORS.globalBlue, fontWeight: 700}}
              >
                {remaining}s
              </Typography>
            ) : null}
            <Box display="flex" justifyContent="center" gap={2} flexWrap="wrap">
              <Button
                variant="outlined"
                size="medium"
                disabled={submitting}
                onClick={onDecline}
                sx={outlinedBtnSx}
              >
                Decline
              </Button>
              <Button
                variant="contained"
                size="medium"
                disabled={submitting}
                onClick={onAccept}
                sx={containedBtnSx}
              >
                Accept
              </Button>
            </Box>
          </>
        )}
      </Box>
    </ConfirmModal>
  );
};

AiCallIncomingPrompt.propTypes = {
  prompt: PropTypes.shape({
    open: PropTypes.bool,
    status: PropTypes.oneOf([null, 'passed']),
    hasCall: PropTypes.bool,
    expiresAt: PropTypes.number,
    submitting: PropTypes.bool,
    onAccept: PropTypes.func.isRequired,
    onDecline: PropTypes.func.isRequired,
    onExpire: PropTypes.func.isRequired,
    onCloseStatus: PropTypes.func.isRequired,
  }).isRequired,
};

export default AiCallIncomingPrompt;
